"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Button from "@/components/ui/Button";
import { useFormErrors } from "@/hooks/useFormErrors";
import { useAuthGuard } from "@/hooks/useAuthGuard";
import { FormInput } from "@/components/forms/FormInput";
import { requestAccountDeletionAction } from "@/lib/actions/account-deletion-actions";
import Loading from "@/components/shared/Loading";
import {
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

export default function ExclusaoDeContaPage() {
  const router = useRouter();
  const { isLoading: authLoading } = useAuthGuard({ redirectTo: "/login" });
  const { formErrors, setFormErrors, clearErrors } = useFormErrors();

  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "error" | "warning";
    message: string;
  } | null>(null);

  // Protege a página - usuário deve estar autenticado
  if (authLoading) {
    return <Loading />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    setStatus(null);
    setLoading(true);

    // Validações
    const errors: Record<string, string> = {};

    if (!password.trim()) {
      errors.password = "Senha é obrigatória";
    }

    if (confirmText.trim().toLowerCase() !== "excluir minha conta") {
      errors.confirmText = 'Digite exatamente "EXCLUIR MINHA CONTA"';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const result = await requestAccountDeletionAction({
        password,
        reason: reason.trim() || undefined,
      });

      if (result.success) {
        setStatus({
          type: "success",
          message: result.message || "Solicitação enviada com sucesso!",
        });
        setShowConfirmation(true);

        // Limpar formulário
        setPassword("");
        setConfirmText("");
        setReason("");
      } else {
        setStatus({
          type: "error",
          message: result.error || "Erro ao processar solicitação",
        });
      }
    } catch (error) {
      console.error("Erro ao solicitar exclusão:", error);
      setStatus({
        type: "error",
        message: "Erro ao processar solicitação. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (showConfirmation) {
    return (
      <div className="min-h-[calc(100vh-80px)] bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Solicitação Registrada
            </h2>
            <p className="text-gray-600 mb-6">
              Sua solicitação de exclusão de conta foi registrada com sucesso.
              Processaremos seu pedido em até 30 dias úteis.
            </p>
            <p className="text-gray-600 mb-6">
              Você receberá um e-mail de confirmação quando a exclusão for
              concluída.
            </p>
            <p className="text-sm text-gray-500 mb-8">
              Durante este período, você ainda pode acessar sua conta
              normalmente.
            </p>
            <Button onClick={() => router.push("/")} variant="primary">
              Voltar para a Página Inicial
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <TrashIcon className="h-6 w-6 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              Exclusão de Conta
            </h1>
          </div>

          {/* Warning Alert */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong className="font-bold">Atenção!</strong> Esta ação é{" "}
                  <strong>irreversível</strong>. Todos os seus dados serão
                  permanentemente excluídos.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              O que será excluído:
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-600">
              <li>Seus dados pessoais (nome, e-mail, telefone)</li>
              <li>Empresas e serviços cadastrados por você</li>
              <li>Eventos criados</li>
              <li>Histórico de atividades</li>
              <li>Acesso ao aplicativo</li>
            </ul>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
              <p className="text-sm text-blue-700">
                <strong className="font-bold">Importante:</strong> Dados
                obrigatórios por lei (registros de transações, etc.) serão
                mantidos pelo período legal exigido.
              </p>
            </div>
          </div>

          {status && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                status.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : status.type === "error"
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : "bg-yellow-50 text-yellow-700 border border-yellow-200"
              }`}
            >
              {status.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-4">
                Confirme sua identidade:
              </h3>

              <div className="space-y-4">
                <FormInput
                  id="password"
                  name="password"
                  type="password"
                  label="Senha"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  error={formErrors.password}
                  placeholder="Digite sua senha para confirmar"
                  required
                  disabled={loading}
                />

                <div>
                  <label
                    htmlFor="reason"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Motivo (opcional)
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={reason}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setReason(e.target.value)
                    }
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Por favor, conte-nos o motivo da exclusão (isso nos ajuda a melhorar)"
                    disabled={loading}
                  />
                </div>

                <FormInput
                  id="confirmText"
                  name="confirmText"
                  type="text"
                  label='Digite "EXCLUIR MINHA CONTA" para confirmar'
                  value={confirmText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setConfirmText(e.target.value)
                  }
                  error={formErrors.confirmText}
                  placeholder="EXCLUIR MINHA CONTA"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => router.back()}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="danger"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? "Processando..." : "Confirmar Exclusão"}
                </Button>
              </div>
            </div>
          </form>

          <div className="mt-6 text-sm text-gray-500 text-center">
            <p>Prazo de processamento: até 30 dias úteis após a confirmação.</p>
            <p className="mt-2">
              Dúvidas? Entre em contato:{" "}
              <a
                href="/contato"
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Fale Conosco
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
